/**
 * kanban/filters/model — the pure filter state, storage, and predicate logic
 * for KanbanFiltersBar. Extracted VERBATIM from the former monolith during
 * the <500-line decomposition; re-exported by the orchestrator so existing
 * importers keep their `from './KanbanFiltersBar'` paths. Pure I→O so views
 * (board / table / calendar / gantt) and the ⌘F find share ONE definition.
 *
 * Moved from components/filters-bar/model.ts (2026-07-05, boundary sweep):
 * pure logic must live BELOW components/ so hooks can value-import it
 * without violating the quotech-frames/boundaries layer contract. A
 * re-export shim remains at the old path for existing importers.
 */

import { tokenizeSearchQuery } from '../../components/global-search/matching'
import type { Card } from '../types'
import { addDaysYmd, todayLocalYmd } from '../utils/localDate'

// ---------------------------------------------------------------------------
// Filter state shape — exported so the parent can persist + apply.
// ---------------------------------------------------------------------------
export type DueQuickFilter = 'overdue' | 'today' | 'this_week' | 'no_date'
/**
 * Card status values that the operator can pick from in the filters
 * bar. ``ARCHIVED`` is included so the operator can OPT IN to seeing
 * archived cards on the board canvas — by default they are hidden
 * (Trello-style; the board snapshot from Trello includes archived
 * cards in the same lane as `closed=true` and our Trello bridge
 * preserves that as ``Card.status=ARCHIVED``, so a Trello-mirror
 * Inbox that has 104 open + 341 closed cards would render 445 cards
 * on our side without this default-hide rule). Operators who need to
 * see archived cards (e.g. to un-archive one) flip the Archived pill
 * on.
 *
 * See ``filterCards`` below for the actual default-hide semantics
 * and ``loadFilterState`` for the persisted-state guard list.
 */
export type StatusFilter = 'OPEN' | 'BLOCKED' | 'SNOOZED' | 'DONE' | 'ARCHIVED'

export interface KanbanFilterState {
  search: string
  memberIds: number[]
  tagNames: string[]
  due: DueQuickFilter[]
  status: StatusFilter[]
  /** ARC-5 U18 "Focus: unseen" — keep only cards with at least one linked
   *  email the CURRENT user has neither opened anywhere nor already has in
   *  their own mailbox. Needs `FilterCardsContext.unseenByCard` (the board
   *  passes the per-user aggregate); without the map the toggle is a
   *  no-op so views that never fetch it (table/calendar) stay unchanged. */
  focusUnseen: boolean
}

export const EMPTY_FILTER_STATE: KanbanFilterState = {
  search: '',
  memberIds: [],
  tagNames: [],
  due: [],
  status: [],
  focusUnseen: false,
}

const FILTER_STORAGE_PREFIX = 'kanban:filters:'

/** Pull the saved filter state for a board, or `EMPTY_FILTER_STATE`. */
export function loadFilterState(boardId: string): KanbanFilterState {
  try {
    const raw = window.localStorage?.getItem(FILTER_STORAGE_PREFIX + boardId)
    if (!raw) return EMPTY_FILTER_STATE
    const parsed = JSON.parse(raw)
    // Guard against corrupted localStorage from older shapes.
    return {
      search: typeof parsed.search === 'string' ? parsed.search : '',
      memberIds: Array.isArray(parsed.memberIds) ? parsed.memberIds.filter((x: unknown) => typeof x === 'number') : [],
      tagNames: Array.isArray(parsed.tagNames) ? parsed.tagNames.filter((x: unknown) => typeof x === 'string') : [],
      due: Array.isArray(parsed.due) ? parsed.due.filter((x: unknown): x is DueQuickFilter => typeof x === 'string' && ['overdue', 'today', 'this_week', 'no_date'].includes(x)) : [],
      status: Array.isArray(parsed.status) ? parsed.status.filter((x: unknown): x is StatusFilter => typeof x === 'string' && ['OPEN', 'BLOCKED', 'SNOOZED', 'DONE', 'ARCHIVED'].includes(x)) : [],
      focusUnseen: parsed.focusUnseen === true,
    }
  } catch {
    return EMPTY_FILTER_STATE
  }
}

export function saveFilterState(boardId: string, state: KanbanFilterState): void {
  try {
    window.localStorage?.setItem(FILTER_STORAGE_PREFIX + boardId, JSON.stringify(state))
  } catch {
    // no-op (incognito etc.)
  }
}

// ---------------------------------------------------------------------------
// Search predicate — extracted so both `filterCards` (the in-board
// filter box) and the board's in-page find (⌘F) share ONE definition
// of "this card matches the query". Pure I→O: (card, lowercased query,
// optional context) → boolean.
//
// Field coverage (widened from the old title/summary/tag-only set):
//   - title, summary, tag names, email sender (always available on a Card)
//   - blocked_reason                       (operators search by why a
//                                           card is stuck)
//   - job id (`#1784` / `1784`)            (search by job number)
//   - assignee display names               (ctx.memberNames — the Card
//                                           only carries ids, so the
//                                           board passes the id→name map)
//   - lane name                            (ctx.laneNameByCardId — the
//                                           Card only carries a lane id)
// ---------------------------------------------------------------------------
export interface FilterCardsContext {
  /** Member id → display name, so search can match an assignee by
   *  name (the Card list shape only carries `assigned_user_ids`). */
  memberNames?: Map<number, string>
  /** Card id → lane name, so search can match the lane a card sits in
   *  (the Card list shape only carries the lane id). */
  laneNameByCardId?: Map<string, string>
  /** ARC-5 U18 — card id → the CURRENT user's unseen-email count (from
   *  `useBoardUnseenEmails`). Consumed only when `focusUnseen` is on;
   *  omitted map ⇒ the focus toggle degrades to a no-op (surfaces that
   *  never fetch the aggregate keep their existing behaviour). */
  unseenByCard?: Record<string, number>
  /** True only after the unseen aggregate has settled successfully.
   *  Without this, a loading `{}` map would hide every card and make
   *  "New emails" look broken. */
  unseenReady?: boolean
}

export function cardMatchesSearch(
  c: Card,
  q: string,
  ctx?: FilterCardsContext,
): boolean {
  if (!q) return true
  // Multi-term (G7): split on whitespace and require EVERY token to
  // match SOME field, in any order — so "hudson 60" finds "60 Hudson"
  // and a token may match a different field than its neighbour (title
  // + assignee). Mirrors the backend `_card_token_q` AND-per-token
  // semantics so the in-board box and "Search all boards" agree.
  // W4a — tokenizer unified with the canonical global-search matching
  // module (same split/trim/lowercase everywhere).
  const tokens = tokenizeSearchQuery(q)
  if (tokens.length === 0) return true
  return tokens.every((tok) => cardMatchesToken(c, tok, ctx))
}

/** Does ONE token match ANY searchable field of the card? `tok` is
 *  assumed already lowercased (callers lowercase the whole query). */
function cardMatchesToken(
  c: Card,
  tok: string,
  ctx?: FilterCardsContext,
): boolean {
  if (c.title?.toLowerCase().includes(tok)) return true
  if (c.summary?.toLowerCase().includes(tok)) return true
  if (c.primary_email_from_name?.toLowerCase().includes(tok)) return true
  if (c.primary_email_from_address?.toLowerCase().includes(tok)) return true
  if (c.tags?.some((t) => t.tag.name.toLowerCase().includes(tok))) return true
  if (c.blocked_reason?.toLowerCase().includes(tok)) return true
  if (c.job_id != null) {
    const job = String(c.job_id)
    if (job.includes(tok) || `#${job}`.includes(tok)) return true
  }
  if (ctx?.memberNames && c.assigned_user_ids?.length) {
    for (const id of c.assigned_user_ids) {
      const name = ctx.memberNames.get(id)
      if (name && name.toLowerCase().includes(tok)) return true
    }
  }
  if (ctx?.laneNameByCardId) {
    const laneName = ctx.laneNameByCardId.get(c.id)
    if (laneName && laneName.toLowerCase().includes(tok)) return true
  }
  return false
}

// ---------------------------------------------------------------------------
// filterCards — apply a KanbanFilterState to a card list. Pure
// function so views (table, calendar, gantt) can use it too. Search
// matches via `cardMatchesSearch` (title + summary + tag + blocked
// reason + job + assignee/lane names when the optional context is
// supplied). The optional `ctx` is additive — existing 2-arg callers
// keep the legacy title/summary/tag/blocked/job coverage.
// ---------------------------------------------------------------------------
export function filterCards(
  cards: Card[],
  f: KanbanFilterState,
  ctx?: FilterCardsContext,
): Card[] {
  const q = f.search.trim().toLowerCase()
  const memberSet = new Set(f.memberIds)
  const tagSet = new Set(f.tagNames.map((n) => n.toLowerCase()))
  const dueSet = new Set(f.due)
  const statusSet = new Set(f.status)
  // Pure local-calendar string compares (UAT P0 timezone sweep): the old
  // ms math parsed `due_date` via `new Date('YYYY-MM-DD')` — UTC midnight —
  // so a card due TODAY landed in "Overdue" for any UTC− operator, and the
  // +7d week boundary drifted an hour across DST.
  const todayYmd = todayLocalYmd()
  const weekEndYmd = addDaysYmd(todayYmd, 7)

  return cards.filter((c) => {
    // text search (title / summary / tag / blocked reason / job /
    // assignee + lane name when context is supplied)
    if (q && !cardMatchesSearch(c, q, ctx)) return false

    // ARC-5 U18 "Focus: unseen" — composes with every other filter
    // (AND semantics, same as member/tag). Only enforced when the
    // board's per-user aggregate has settled — a loading `{}` must
    // NOT wipe the board (operator: "New emails does nothing").
    if (f.focusUnseen && ctx?.unseenReady === true && ctx.unseenByCard) {
      if (!((ctx.unseenByCard[c.id] ?? 0) > 0)) return false
    }

    // member assignment (must intersect)
    if (memberSet.size > 0) {
      const ids = c.assigned_user_ids ?? []
      if (!ids.some((id) => memberSet.has(id))) return false
    }

    // tag (must include at least one)
    if (tagSet.size > 0) {
      const has = c.tags?.some((t) => tagSet.has(t.tag.name.toLowerCase()))
      if (!has) return false
    }

    // status — multi-select.
    //
    // Two-layer semantics so the default render matches what an
    // operator sees in Trello (closed cards hidden) without
    // requiring them to click anything:
    //
    //   1. EMPTY selection (default) → show every NON-archived card.
    //      ARCHIVED cards are hidden by default. This is the bug
    //      fix for "Benko board deviates from Trello": our DB
    //      correctly holds ARCHIVED cards (the Trello bridge sets
    //      Card.status=ARCHIVED when Trello marks a card closed),
    //      but the FE used to render them in their original lane,
    //      inflating lane counts vs. Trello's own UI by 3-5x for
    //      heavily-curated boards like BenkoDesk.
    //   2. NON-EMPTY selection → ONLY the picked statuses pass.
    //      Operator can pick 'ARCHIVED' as a status to un-hide
    //      archived cards (with or without other statuses).
    if (statusSet.size === 0) {
      if (c.status === 'ARCHIVED') return false
    } else if (!statusSet.has(c.status as StatusFilter)) {
      return false
    }

    // due-date quick filters — UNION semantics. Empty = all allowed.
    if (dueSet.size > 0) {
      const due = c.due_date ? c.due_date.slice(0, 10) : null
      let dueHit = false
      if (dueSet.has('no_date') && !due) dueHit = true
      if (dueSet.has('overdue') && due !== null && due < todayYmd && c.status !== 'DONE' && c.status !== 'ARCHIVED') dueHit = true
      if (dueSet.has('today') && due !== null && due === todayYmd) dueHit = true
      if (dueSet.has('this_week') && due !== null && due >= todayYmd && due <= weekEndYmd) dueHit = true
      if (!dueHit) return false
    }

    return true
  })
}

/** Count the number of active filter dimensions (used for the
 *  badge on the bar's collapse button). */
export function activeFilterCount(f: KanbanFilterState): number {
  let n = 0
  if (f.search.trim()) n++
  if (f.memberIds.length) n++
  if (f.tagNames.length) n++
  if (f.due.length) n++
  if (f.status.length) n++
  if (f.focusUnseen) n++
  return n
}
